import { Injectable } from "@angular/core";
import * as semver from "semver";
import * as moment from "moment";

import { TranslateService } from "../../common/translate";
import { GatewaysService } from "./gateways.service";
import { SdpIcon } from "../../common/icon/models";
import { GatewayView } from "./gateway-view";
import { GatewayStatus } from "./models";
import { createDateTimeDisplayCallback } from "../../util";

@Injectable()
export class GatewaysStatusService {

	constructor(
		private translateService: TranslateService,
		private gatewaysService: GatewaysService
	) { }

	public async setGatewayStatus(gateway: GatewayView): Promise<GatewayStatus[]> {
		const statuses: GatewayStatus[] = [];
		const heartbeatStatus = this.getHeartbeatStatus(gateway);
		if (!!heartbeatStatus) {
			statuses.push(heartbeatStatus);
		}

		const versionStatus = await this.getGatewayVersionStatus(gateway);
		if (!!versionStatus) {
			statuses.push(versionStatus);
		}

		const clockSkewStatus = this.getClockSkewStatus(gateway);
		if (!!clockSkewStatus) {
			statuses.push(clockSkewStatus);
		}

		gateway.statuses = statuses;
		return statuses;
	}

	public async getGatewayVersionStatus(gateway: GatewayView): Promise<GatewayStatus | undefined> {
		const currentVersion = semver.coerce((await this.gatewaysService.getAvailableVersion()).version);
		if (!!gateway.gatewayVersion && !!currentVersion && semver.lt(gateway.gatewayVersion, currentVersion)) {
			return {
				type: "version",
				icon: getWarningStatusIcon(),
				text: this.translateService.translate("gateway.version.new"),
				tooltip: "gateway.version.new"
			};
		} else {
			return undefined;
		}
	}

	public getHeartbeatStatus(gateway: GatewayView): GatewayStatus {
		const WARNING_TIME = moment.duration(2, "minutes");
		const ERROR_TIME = moment.duration(5, "minutes");
		const text = createDateTimeDisplayCallback(this.translateService.translate("heartbeat.text.never"))(`${gateway.lastHeartbeatTimestamp}`);

		function getGoodStatus(): GatewayStatus {
			return {
				type: "heartbeat",
				icon: getGoodStatusIcon(),
				text: text,
				tooltip: "heartbeat.tooltip.good"
			};
		}

		function getWarnStatus(): GatewayStatus {
			return {
				type: "heartbeat",
				icon: getWarningStatusIcon(),
				text: text,
				tooltip: "heartbeat.tooltip.warning"
			};
		}

		function getBadStatus(): GatewayStatus {
			return {
				type: "heartbeat",
				icon: getBadStatusIcon(),
				text: text,
				tooltip: "heartbeat.tooltip.bad"
			};
		}

		if (!!gateway.gatewayVersion && semver.lt(gateway.gatewayVersion, "0.2.0")) {
			// TODO when 0.1.0 and 0.1.1 are fully deprecated get rid of this
			// gateway is too old to heartbeat, placeholder text
			return {
				type: "heartbeat",
				icon: getGoodStatusIcon(),
				text: "heartbeat.text.old",
				tooltip: "heartbeat.tooltip.good"
			};
		}

		if (!gateway.lastHeartbeatTimestamp) {
			return getBadStatus();
		}

		const diff = moment().diff(moment(gateway.lastHeartbeatTimestamp));
		if (diff < WARNING_TIME.asMilliseconds()) {
			return getGoodStatus();
		}
		if (diff < ERROR_TIME.asMilliseconds()) {
			return getWarnStatus();
		}
		return getBadStatus();
	}

	public getClockSkewStatus(gateway: GatewayView): GatewayStatus | undefined {
		const clockSkew = Math.abs(gateway.clockSkew);
		const clockSkewString = `${Math.round(clockSkew / 1000)}s`;

		if (clockSkew < 1000) {
			return undefined;
		} else if (clockSkew < 3000) {
			return {
				type: "clockskew",
				icon: getWarningStatusIcon(),
				text: this.translateService.translate(
					"clockskew.text.warning",
					{
						clockSkew: clockSkewString
					}
				),
				tooltip: "clockskew.tooltip"
			};
		} else {
			return {
				type: "clockskew",
				icon: getBadStatusIcon(),
				text: this.translateService.translate(
					"clockskew.text.bad",
					{
						clockSkew: clockSkewString
					}
				),
				tooltip: "clockskew.tooltip"
			};
		}
	}
}

function getGoodStatusIcon(): SdpIcon.Icon {
	return {
		type: "fa",
		name: "check-circle",
		cssClass: "good sdp-gateway-status-icon"
	};
}

function getWarningStatusIcon(): SdpIcon.Icon {
	return {
		type: "fa",
		name: "question-circle",
		cssClass: "warning sdp-gateway-status-icon"
	};
}

function getBadStatusIcon(): SdpIcon.Icon {
	return {
		type: "fa",
		name: "exclamation-circle",
		cssClass: "bad sdp-gateway-status-icon"
	};
}
